import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, UserX, User, Users, Loader2, Eye, RefreshCw, Mail, AlertTriangle, Archive, AlertCircle, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DidntRenew = () => {
    const { user } = useAuth();
    const isBranchManager = user?.role === 'branch_manager';
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';

    const [tab, setTab] = useState('guardians');
    const [students, setStudents] = useState([]);
    const [guardians, setGuardians] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [branchFilter, setBranchFilter] = useState('__all__');
    const [programFilter, setProgramFilter] = useState('__all__');
    const branches = React.useMemo(
        () => Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort(),
        [programs]
    );

    const [renewDialog, setRenewDialog] = useState(null); // { kind:'guardian'|'student', target }
    const [unregisterDialog, setUnregisterDialog] = useState(null); // { kind:'guardian'|'student', target }
    const [processing, setProcessing] = useState(false);
    const [reminderPreview, setReminderPreview] = useState(null); // { count, recipients }
    const [sendingReminders, setSendingReminders] = useState(false);
    const [reminderResult, setReminderResult] = useState(null);
    const [smsSending, setSmsSending] = useState(false);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [studentsRes, guardiansRes, progRes] = await Promise.all([
                axios.get(`${API_URL}/api/didnt-renew`, { withCredentials: true }),
                axios.get(`${API_URL}/api/didnt-renew/guardians`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
            ]);
            setStudents(studentsRes.data);
            setGuardians(guardiansRes.data);
            setPrograms(progRes.data || []);
        } catch { toast.error('Failed to load didn\'t-renew list'); }
        finally { setLoading(false); }
    };

    const openRenewGuardian = (g) => setRenewDialog({ kind: 'guardian', target: g });
    const openRenewStudent = (s) => setRenewDialog({ kind: 'student', target: s });
    const openUnregisterGuardian = (g) => setUnregisterDialog({ kind: 'guardian', target: g });
    const openUnregisterStudent = (s) => setUnregisterDialog({ kind: 'student', target: s });

    const submitUnregister = async () => {
        if (!unregisterDialog) return;
        setProcessing(true);
        try {
            // Resolve target student ids
            let studentIds = [];
            if (unregisterDialog.kind === 'student') {
                studentIds = [unregisterDialog.target.id];
            } else {
                // Guardian: unregister every one of their didn't-renew students
                studentIds = students
                    .filter(s => s.parent && s.parent.id === unregisterDialog.target.id)
                    .map(s => s.id);
            }
            if (studentIds.length === 0) throw new Error('No students to unregister');
            await Promise.all(studentIds.map(sid =>
                axios.put(`${API_URL}/api/students/${sid}/status`, { status: 'unregistered' }, { withCredentials: true })
            ));
            toast.success(`${studentIds.length} student(s) moved to Unregistered`);
            setUnregisterDialog(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'Failed to unregister');
        } finally { setProcessing(false); }
    };

    const submitRenew = async () => {
        if (!renewDialog) return;
        setProcessing(true);
        try {
            const body = renewDialog.kind === 'guardian'
                ? { parent_id: renewDialog.target.id }
                : { student_ids: [renewDialog.target.id] };
            const r = await axios.post(`${API_URL}/api/didnt-renew/mark-renewed`, body, { withCredentials: true });
            toast.success(r.data.message || 'Renewed');
            setRenewDialog(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to mark as renewed');
        } finally { setProcessing(false); }
    };

    const openReminders = async () => {
        try {
            const r = await axios.get(`${API_URL}/api/didnt-renew/reminder/preview`, { withCredentials: true });
            setReminderResult(null);
            setReminderPreview(r.data);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to preview'); }
    };

    const sendReminders = async () => {
        setSendingReminders(true);
        try {
            const r = await axios.post(`${API_URL}/api/didnt-renew/reminder/send`, {}, { withCredentials: true });
            setReminderResult(r.data);
            toast.success(`${r.data.sent} reminder(s) sent`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
        finally { setSendingReminders(false); }
    };

    const matchesBranchProgram = (row) => {
        if (programFilter === '__all__' && branchFilter === '__all__') return true;
        if (row.program_id !== undefined) {
            // Student row
            if (programFilter !== '__all__' && row.program_id !== programFilter) return false;
            if (branchFilter !== '__all__' && (row.branch || '') !== branchFilter) return false;
            return true;
        }
        // Guardian row
        if (programFilter !== '__all__' && !(row.program_ids || []).includes(programFilter)) return false;
        if (branchFilter !== '__all__' && !(row.branches || []).includes(branchFilter)) return false;
        return true;
    };

    const normalizeMobile = (m) => (m || '').replace(/[^0-9+]/g, '');
    const q = searchQuery.trim().toLowerCase();
    const qDigits = q.replace(/[^0-9+]/g, '');
    const mobileMatch = (mobiles) => qDigits && mobiles.some(m => normalizeMobile(m).includes(qDigits));

    const filteredStudents = students.filter(s => matchesBranchProgram(s) && (
        !q || (s.first_name || '').toLowerCase().includes(q) ||
        (s.last_name || '').toLowerCase().includes(q) ||
        (s.reg_code || '').toLowerCase().includes(q) ||
        mobileMatch([s.parent?.mobile, s.parent?.guardian2_mobile])
    ));
    const filteredGuardians = guardians.filter(g => matchesBranchProgram(g) && (
        !q || (g.first_name || '').toLowerCase().includes(q) ||
        (g.last_name || '').toLowerCase().includes(q) ||
        (g.reg_code || '').toLowerCase().includes(q) ||
        (g.email || '').toLowerCase().includes(q) ||
        mobileMatch([g.mobile, g.guardian2_mobile])
    ));

    if (loading) {
        return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div></DashboardLayout>;
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="didnt-renew-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Didn&apos;t Renew</h1>
                        <p className="text-[#4B5563]">Guardians &amp; students who haven&apos;t yet renewed for the next school year</p>
                    </div>
                    <Badge variant="secondary" className="w-fit" data-testid="didnt-renew-count-badge"><UserX className="h-4 w-4 mr-1" />{filteredGuardians.length} guardians · {filteredStudents.length} students</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={openReminders} data-testid="open-reminder-dialog">
                        <Mail className="h-4 w-4 mr-2" /> Send Renewal Reminder to All
                    </Button>
                    <Button
                        className="bg-[#064E3B] hover:bg-[#047857] text-white"
                        disabled={smsSending || filteredGuardians.length === 0}
                        onClick={async () => {
                            if (!window.confirm(`Send SMS renewal reminder to ${filteredGuardians.length} guardian${filteredGuardians.length === 1 ? '' : 's'}?`)) return;
                            setSmsSending(true);
                            try {
                                const res = await axios.post(`${API_URL}/api/sms/renewal-reminder`, { parent_ids: filteredGuardians.map(g => g.id) }, { withCredentials: true });
                                toast.success(`SMS sent: ${res.data.sent}${res.data.failed ? `, failed: ${res.data.failed}` : ''}`);
                            } catch { toast.error('Failed to send SMS reminders'); }
                            finally { setSmsSending(false); }
                        }}
                        data-testid="sms-renewal-reminder-btn"
                    >
                        {smsSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />} SMS Reminder to All ({filteredGuardians.length})
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => {
                            const url = tab === 'students'
                                ? `${API_URL}/api/didnt-renew/students/export`
                                : `${API_URL}/api/didnt-renew/guardians/export`;
                            // Use fetch to include cookies then trigger download
                            fetch(url, { credentials: 'include' }).then(async r => {
                                if (!r.ok) { toast.error('Export failed'); return; }
                                const blob = await r.blob();
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(blob);
                                link.download = tab === 'students' ? 'didnt_renew_students.xlsx' : 'didnt_renew_guardians.xlsx';
                                link.click();
                                URL.revokeObjectURL(link.href);
                            });
                        }}
                        data-testid="didnt-renew-export-btn"
                    >
                        <Archive className="h-4 w-4 mr-2" /> Export {tab === 'students' ? 'Students' : 'Guardians'} Excel
                    </Button>
                </div>

                <Card className="card-surface"><CardContent className="p-4">
                    <div className={`grid grid-cols-1 gap-3 ${isBranchManager ? 'md:grid-cols-1' : 'md:grid-cols-3'}`}>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                            <Input placeholder="Search by name, email, mobile or reg code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="didnt-renew-search" />
                        </div>
                        {!isBranchManager && (
                            <>
                                <Select value={programFilter} onValueChange={setProgramFilter}>
                                    <SelectTrigger data-testid="didnt-renew-program-filter"><SelectValue placeholder="All programs" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">All programs</SelectItem>
                                        {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Select value={branchFilter} onValueChange={setBranchFilter}>
                                    <SelectTrigger data-testid="didnt-renew-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">All branches</SelectItem>
                                        {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </>
                        )}
                    </div>
                </CardContent></Card>

                <Tabs value={tab} onValueChange={setTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="guardians" data-testid="tab-didnt-renew-guardians"><Users className="h-4 w-4 mr-2" />Guardians ({filteredGuardians.length})</TabsTrigger>
                        <TabsTrigger value="students" data-testid="tab-didnt-renew-students"><User className="h-4 w-4 mr-2" />Students ({filteredStudents.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="guardians">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredGuardians.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Code</TableHead>
                                        <TableHead>Guardian</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Mobile</TableHead>
                                        <TableHead>Students</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredGuardians.map(g => (
                                            <TableRow key={g.id} className="hover:bg-[#FAF9F6]" data-testid={`didnt-renew-guardian-row-${g.id}`}>
                                                <TableCell><Badge variant="outline" className="font-mono tabular-nums">{g.reg_code}</Badge></TableCell>
                                                <TableCell className="font-medium cursor-pointer" onClick={() => navigate(`${basePath}/parents/${g.id}`)}>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span>{g.first_name} {g.last_name}</span>
                                                        {g.has_previous_debt && (
                                                            <Badge className="bg-[#DC2626] hover:bg-[#DC2626] text-white text-[10px] px-1.5 py-0 gap-1" data-testid={`prev-debt-badge-${g.id}`}>
                                                                <AlertCircle className="h-3 w-3" />
                                                                {g.previous_debt_amount ? `Debt €${g.previous_debt_amount}` : 'Previous Debt'}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm">{g.email}</TableCell>
                                                <TableCell className="text-sm">{g.mobile}</TableCell>
                                                <TableCell><div className="flex items-center gap-2"><Badge className="bg-[#6B7280]">{g.students_count}</Badge><span className="text-xs text-[#4B5563] line-clamp-1">{(g.student_names || []).join(', ')}</span></div></TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1.5">
                                                        <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/parents/${g.id}`)} data-testid={`view-guardian-${g.id}`}><Eye className="h-4 w-4" /></Button>
                                                        <Button size="sm" variant="outline" className="text-[#10B981] border-[#10B981]/40 hover:bg-[#10B981]/10" onClick={() => openRenewGuardian(g)} data-testid={`renew-guardian-${g.id}`}>
                                                            <RefreshCw className="h-4 w-4 mr-1" />Renew
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-[#B45309] border-[#F59E0B]/40 hover:bg-[#F59E0B]/10" onClick={() => openUnregisterGuardian(g)} data-testid={`unregister-guardian-${g.id}`}>
                                                            <Archive className="h-4 w-4 mr-1" />Unregister
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="flex flex-col items-center justify-center py-12"><Users className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">Every guardian has renewed at least one child</p></div>}
                        </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="students">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredStudents.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Parent</TableHead>
                                        <TableHead>Program</TableHead>
                                        <TableHead>Class</TableHead>
                                        <TableHead>Date of Birth</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredStudents.map(student => (
                                            <TableRow key={student.id} className="hover:bg-[#FAF9F6]" data-testid={`didnt-renew-student-row-${student.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#6B7280]/10 flex items-center justify-center"><User className="h-5 w-5 text-[#6B7280]" /></div>
                                                        <div>
                                                            <p className="font-medium text-[#1F2937]">{student.first_name} {student.last_name}</p>
                                                            <p className="text-xs text-[#4B5563] tabular-nums">{student.reg_code}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{student.parent && <div><p className="text-sm">{student.parent.first_name} {student.parent.last_name}</p><p className="text-xs text-[#4B5563]">{student.parent.email}</p></div>}</TableCell>
                                                <TableCell><Badge variant="outline">{student.program_name || '—'}</Badge></TableCell>
                                                <TableCell className="text-sm">{student.class_name || '—'}</TableCell>
                                                <TableCell className="tabular-nums">{student.date_of_birth}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1.5">
                                                        <Button size="sm" variant="outline" className="text-[#10B981] border-[#10B981]/40 hover:bg-[#10B981]/10" onClick={() => openRenewStudent(student)} data-testid={`renew-student-${student.id}`}>
                                                            <RefreshCw className="h-4 w-4 mr-1" />Renew
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="text-[#B45309] border-[#F59E0B]/40 hover:bg-[#F59E0B]/10" onClick={() => openUnregisterStudent(student)} data-testid={`unregister-student-${student.id}`}>
                                                            <Archive className="h-4 w-4 mr-1" />Unregister
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="flex flex-col items-center justify-center py-12"><UserX className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No students awaiting renewal</p></div>}
                        </CardContent></Card>
                    </TabsContent>
                </Tabs>

                <Dialog open={!!reminderPreview} onOpenChange={(o) => { if (!o) { setReminderPreview(null); setReminderResult(null); } }}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Mail className="h-5 w-5 text-[#7C3AED]" />Send Renewal Reminders</DialogTitle>
                            <DialogDescription>A lightweight email will be sent to each non-renewed guardian below with password-reset instructions.</DialogDescription>
                        </DialogHeader>
                        {reminderPreview && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-[#F59E0B]"><AlertTriangle className="h-4 w-4" /><span>{reminderPreview.count} recipient(s)</span></div>
                                <div className="max-h-52 overflow-y-auto text-xs border rounded p-2 bg-[#FAF9F6]">
                                    {reminderPreview.recipients.map(r => (
                                        <div key={r.id}>{r.first_name} {r.last_name} <span className="text-[#6B7280]">({r.email})</span></div>
                                    ))}
                                    {reminderPreview.recipients.length === 0 && <div className="text-center text-[#6B7280] py-3">No recipients — everyone has renewed.</div>}
                                </div>
                                {reminderResult && (
                                    <div className="text-xs bg-[#F0FDF4] border border-[#BBF7D0] p-2 rounded">
                                        Sent {reminderResult.sent} / {reminderResult.total} · Failed {reminderResult.failed}
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setReminderPreview(null); setReminderResult(null); }}>Cancel</Button>
                            <Button
                                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                                onClick={sendReminders}
                                disabled={sendingReminders || (reminderPreview?.count || 0) === 0 || !!reminderResult}
                                data-testid="confirm-send-reminders"
                            >
                                {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                                {reminderResult ? 'Sent' : `Send to ${reminderPreview?.count || 0} guardians`}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={!!renewDialog} onOpenChange={(o) => !o && setRenewDialog(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Mark as renewed</DialogTitle>
                            <DialogDescription>
                                {renewDialog?.kind === 'guardian'
                                    ? `All active children of ${renewDialog?.target?.first_name || ''} ${renewDialog?.target?.last_name || ''} will be marked as renewed for next year, keeping their current programs. They will move back to the normal Parents / Students lists.`
                                    : `${renewDialog?.target?.first_name || ''} ${renewDialog?.target?.last_name || ''} will be marked as renewed for next year, keeping their current program.`}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRenewDialog(null)}>Cancel</Button>
                            <Button className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={submitRenew} disabled={processing} data-testid="confirm-mark-renewed">
                                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}Mark as renewed
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={!!unregisterDialog} onOpenChange={(o) => !o && setUnregisterDialog(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Archive className="h-5 w-5 text-[#F59E0B]" />Move to Unregistered</DialogTitle>
                            <DialogDescription>
                                {unregisterDialog?.kind === 'guardian'
                                    ? `All non-renewed children of ${unregisterDialog?.target?.first_name || ''} ${unregisterDialog?.target?.last_name || ''} will be moved to the Unregistered list and will no longer appear in the Students list.`
                                    : `${unregisterDialog?.target?.first_name || ''} ${unregisterDialog?.target?.last_name || ''} will be moved to the Unregistered list and will no longer appear in the Students list.`}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setUnregisterDialog(null)}>Cancel</Button>
                            <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white" onClick={submitUnregister} disabled={processing} data-testid="confirm-unregister">
                                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Archive className="h-4 w-4 mr-2" />}Move to Unregistered
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default DidntRenew;
